/**
 * Byrdsense Design Tokens — SINGLE SOURCE OF TRUTH (shared by both apps).
 *
 * Canonical copy lives in customer-portal at src/design/tokens.js.
 * The onboarding app carries a byte-identical copy at its own src/design/tokens.js.
 * If you edit one, edit both. (Two separate CRA repos/builds; a copied file is the
 * pragmatic "one source" — documented in CLAUDE.md.)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE VISUAL RULE: "the reading, not the machine."
 * The product is an instrument. Ivory paper, navy ink, hairline rules, and one
 * mono typeface for every number. Color is information, not decoration: the only
 * saturated color on a page should be a motor telling you something. If a screen
 * has color that is not a reading, a grade, or a status, it is wrong.
 * ─────────────────────────────────────────────────────────────────────────────
 * ISO STATUS COLOR RULE (do not violate):
 * The `status.*` colors are RESERVED EXCLUSIVELY for machine-status semantics:
 * motor health cards, ISO 20816 grades, live readings, and alerts. They must NEVER
 * be used decoratively (accents, section dividers, hero flourishes, buttons, icons
 * that aren't status). `error` (form validation) is the ONE non-machine exception
 * and intentionally reuses the same red.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ── Ground, surfaces, ink ────────────────────────────────────────────────────
export const color = {
  // The ivory ground is the signature. Everything sits on it; cards are white.
  ground:      '#F6F4EE', // ivory page background
  surface:     '#FFFFFF', // cards, panels, instrument surfaces
  surfaceAlt:  '#EEF1F6', // quiet chips/fills on light ground

  // Navy ink — headings, dark surfaces (sidebar, request band, navy service card).
  ink:         '#0A1B3B',
  inkDeep:     '#08152e', // deeper navy panel
  inkField:    '#0F2A55', // input fill on navy grounds

  // Text roles on the light ground.
  text:        '#3A4D6E', // body copy
  textMuted:   '#5E6F8E', // secondary / captions
  textOnDark:  '#FFFFFF',
  textOnDarkSecondary: '#C9D8EE', // body copy on navy
  textOnDarkMuted:     '#9FB6D6', // labels on navy

  // Hairlines — the structure of the whole system.
  hairline:       'rgba(10,27,59,0.12)',
  hairlineStrong: 'rgba(10,27,59,0.25)',
  hairlineSoft:   'rgba(10,27,59,0.10)',
  grid:           'rgba(10,27,59,0.045)', // 40px instrument grid on ivory

  // ── Blues ──
  // primary   — brand blue: buttons on dark, sparkline B, focus rings.
  // link      — darker blue for text on the ivory/white ground (4.5:1 contrast).
  // accent    — light blue, ONLY on navy surfaces.
  primary:     '#4A90E2',
  primaryHover:'#3f7cc4',
  link:        '#2F6FC4',
  linkHover:   '#0A1B3B',
  accent:      '#78B7FF',
  accentSubtle:'rgba(120,183,255,0.16)', // chip fill on navy
  primarySubtle:'#E3ECF8',               // quiet blue tint on light

  // ── STATUS (ISO 20816, formerly ISO 10816) — SEMANTIC USE ONLY. See rule above. ──
  // Two families: `status` for text/strokes on light, `statusOnDark` for navy.
  status: {
    good:            '#1E7A47', // A / B — on light
    goodOnDark:      '#3DD68C', // A / B — on navy
    watch:           '#9A5B00', // C — text
    watchStroke:     '#F5A524', // C — line / dot / card top border
    attention:       '#C2410C', // D — text
    attentionStroke: '#F0563C', // D — line / card top border
    // F has its own red (Batch B, Pete 2026-10-06), matching the PDF's F
    // (#EF4444). Text uses a darker red for contrast on light surfaces.
    critical:        '#B91C1C', // F — text
    criticalStroke:  '#EF4444', // F — line / card top border (= PDF F)
    // `satisfactory` B reads as blue in the portal card set (not a warning).
    satisfactory:    '#2F6FC4',
  },
  // Tinted chip backgrounds (still status-only).
  statusBg: {
    good:      '#E8F6EE',
    watch:     '#FFF3DD',
    attention: '#FDE7E1',
    critical:  '#FEE2E2',
    satisfactory: '#E3ECF8',
  },
  // ISO zone band fills for the instrument chart.
  zoneBand: {
    good:           '#E8F6EE',
    satisfactory:   '#FFF3DD',
    unsatisfactory: '#FDE7E1',
  },
  zoneLabel: {
    good:           '#1E7A47',
    satisfactory:   '#9A5B00',
    unsatisfactory: '#9A3412',
  },

  // Form validation error — the single sanctioned non-machine use of red.
  error:   '#C2410C',
  errorBg: '#FDE7E1',
};

// ── Spacing scale (4px base) ─────────────────────────────────────────────────
export const space = {
  0: '0', 1: '4px', 2: '8px', 3: '12px', 4: '16px', 5: '20px',
  6: '24px', 8: '32px', 10: '40px', 12: '48px', 16: '64px',
  20: '80px', 24: '96px', 32: '128px',
};

// ── Type ─────────────────────────────────────────────────────────────────────
// Archivo 700/800 for headings (tight tracking), Inter for body, IBM Plex Mono
// for EVERY reading, MAC, job number and eyebrow label. Self-hosted, public/fonts.
export const font = {
  display: "'Archivo', 'Inter', system-ui, sans-serif",
  body:    "'Inter', system-ui, -apple-system, sans-serif",
  mono:    "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  size: {
    xs:  '0.75rem',   // 12 — mono labels, captions
    sm:  '0.875rem',  // 14 — secondary UI
    base:'1rem',      // 16 — body
    lg:  '1.1875rem', // 19 — hero subhead
    xl:  '1.375rem',  // 22 — panel titles
    '2xl':'1.5rem',   // 24 — card titles
    '3xl':'1.875rem', // 30 — page title
    '4xl':'2.375rem', // 38 — section heading
    '5xl':'4.25rem',  // 68 — hero
  },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, black: 800 },
  leading:{ none: 1.0, tight: 1.1, snug: 1.25, normal: 1.5, relaxed: 1.55 },
  tracking:{
    tightest:'-0.025em', // hero
    tight:   '-0.015em', // section headings
    normal:  '0',
    eyebrow: '0.12em',   // mono eyebrow labels
    wide:    '0.14em',
  },
};

// ── Radii ────────────────────────────────────────────────────────────────────
export const radius = {
  none: '0', sm: '6px', base: '8px', md: '10px', lg: '12px',
  xl: '14px', '2xl': '18px', '3xl': '20px',
  full: '9999px', // pills: status chips, step circles
};

// ── Elevation — hairlines do most of the work; shadow lifts instruments only ─
export const shadow = {
  none: 'none',
  sm:  '0 1px 2px rgba(10,27,59,0.06)',
  md:  '0 10px 30px rgba(10,27,59,0.05)',
  lg:  '0 16px 40px rgba(10,27,59,0.18)',
  panel: '0 30px 60px rgba(10,27,59,0.10), 0 2px 6px rgba(10,27,59,0.06)',
};

// ── Motion — minimal. No scroll animations. Short, functional transitions. ──
export const motion = {
  fast: '120ms ease',
  base: '180ms ease',
};

// ── Layout ───────────────────────────────────────────────────────────────────
export const layout = {
  maxWidth:   '1440px',
  gutter:     '72px',
  gutterMd:   '40px',
  gutterSm:   '20px',
  gridSize:   40, // instrument grid pitch, px
};

// ── Grade → visual mapping. One place, used by web AND PDF. ─────────────────
// Zone words match the ISO reference table printed in the reports.
export const grade = {
  A: { letter: 'A', zone: 'good',           fg: color.status.good,         bg: color.statusBg.good,         stroke: color.status.goodOnDark },
  B: { letter: 'B', zone: 'satisfactory',   fg: color.status.satisfactory, bg: color.statusBg.satisfactory, stroke: color.primary },
  C: { letter: 'C', zone: 'unsatisfactory', fg: color.status.watch,        bg: color.statusBg.watch,        stroke: color.status.watchStroke },
  D: { letter: 'D', zone: 'unacceptable',   fg: color.status.attention,    bg: color.statusBg.attention,    stroke: color.status.attentionStroke },
  F: { letter: 'F', zone: 'critical',       fg: color.status.critical,     bg: color.statusBg.critical,     stroke: color.status.criticalStroke },
};

export const tokens = { color, space, font, radius, shadow, motion, layout, grade };
export default tokens;
